import React, { useState, useRef, useEffect, useMemo } from "react";
import { Box, makeStyles, Slide, Typography } from "@material-ui/core";
import { connect } from "react-redux";
import { setExtendableId, setExtendableType } from "../../store/actions/edit";

import Vehicle from "./Vehicle";
import VehiclesToolbar from "./VehiclesToolbar";
import { deselectVehicle, setVehicles } from "../../store/actions/project";
import {
  setCurrentVehicleSelection,
  toggleDisabledVehiclesVisibility
} from "../../store/actions/common";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import { remCalc, compare } from "../../utils/helpers";
import UnsavedChangesDialog from "../dialogs/UnsavedChangesDialog";
import { List } from "react-virtualized";
import { setSelectedVehiclesChange } from "../../store/actions/custom";
import useListKeyboardCommands from "./useListKeyboardCommands";

const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: "#fff"
  },
  vehiclesList: ({ isSelectMode }) => ({
    width: "100% !important",
    "& .ReactVirtualized__Grid__innerScrollContainer": {
      paddingBottom: isSelectMode ? theme.spacing(9) : theme.spacing(0.375)
    }
  }),
  textSecondary: {
    color: theme.overrides.MuiTypography.colorTextSecondary.color
  },
  textLink: {
    color: theme.palette.primary.main,
    textDecoration: "none"
  },
  noVehicles: {
    width: remCalc(255)
  },
  vehicleSearching: {
    fontFamily: "Roboto",
    fontWeight: 400,
    fontSize: theme.typography.pxToRem(14),
    lineHeight: "1rem",
    textAlign: "center",
    paddingTop: theme.typography.pxToRem(40),
    color: "#9D9EA2"
  }
}));

function Vehicles({
  vehicles,
  vehicleTypes,
  solution,
  setExtendableId,
  setExtendableType,
  deselectVehicle,
  saveDisabled,
  currentVehicleSelection,
  disabledVehiclesVisible,
  filteredVehiclesByStatus,
  setCurrentVehicleSelection,
  toggleDisabledVehiclesVisibility,
  selectedVehicles,
  extendableId,
  optionsExpanded,
  setExpanded,
  setSelectedVehiclesChange,
  projectTabValue,
  editType
}) {
  const isSelectMode = currentVehicleSelection.length > 0;
  const classes = useStyles({ isSelectMode });
  const [showDeselect, setShowDeselect] = useState(false);
  const [deselect, setDeselect] = useState({});
  const [changesDialog, setChangesDialog] = useState({ visible: false });
  const [selected, setSelected] = useState(false);
  const [haveHovered, setHaveHovered] = useState(false);
  const listKeyboardCommands = useListKeyboardCommands();

  // Clear stops or routes extendable drawers;
  useEffect(() => {
    if (editType !== "vehicles") {
      setExtendableId(null);
      setExtendableType(null);
    }

    return () => setCurrentVehicleSelection([]);
  }, []);

  const sortVehiclesByName = vehicleTypes => {
    return [...vehicleTypes].sort((vehicle1, vehicle2) => {
      return compare(vehicle1.name, vehicle2.name);
    });
  };

  const [filteredVehicles, setFilteredVehicles] = useState(
    sortVehiclesByName(vehicleTypes)
  );

  const [vehicleFilterText, setVehicleFilterText] = useState("");

  useEffect(() => {
    setFilteredVehicles(sortVehiclesByName(filteredVehicles));

    if (!vehicles.length && !disabledVehiclesVisible) {
      toggleDisabledVehiclesVisibility();
    }
  }, [
    disabledVehiclesVisible,
    vehicleTypes,
    vehicles,
    filteredVehiclesByStatus,
    toggleDisabledVehiclesVisibility
    // currentVehicleSelection
  ]);

  // Virtualized list ref
  const listRef = useRef();

  const editVehicle = id => {
    setExpanded([0, 1]);
    // setCurrentVehicleSelection([]);
    setExtendableType("vehicles");
    setExtendableId(id);
  };

  const warnEdit = id => {
    if (!saveDisabled) {
      setChangesDialog({ visible: true, type: "edit", id: id });
    } else {
      editVehicle(id);
    }
  };

  const lastSelectedVehicle = useRef(null);

  // Calculate selection with Shift + select command
  const getVehiclesSelection = id => {
    const startIndex = vehicleTypes.findIndex(
      vehicleType => vehicleType.id === lastSelectedVehicle.current
    );
    const endIndex = vehicleTypes.findIndex(
      vehicleType => vehicleType.id === id
    );
    const start = Math.min(startIndex, endIndex);
    const end = Math.max(startIndex, endIndex);

    const selectableTypes = vehicles
      .filter(el => el.selected && !el.downgraded)
      .map(el => el.type_id.id);
    return vehicleTypes
      .slice(start, end + 1)
      .filter(vehicleType => !selectableTypes.includes(vehicleType.id))
      .map(vehicleType => vehicleType.id);
  };

  const handleChange = (id, isInCurrentSelection, e) => {
    if (isInCurrentSelection) {
      // deselecting vehicle from CURRENT SELECTION
      let newSelection = [];
      newSelection = currentVehicleSelection.filter(
        vehicleId => vehicleId !== id
      );

      setCurrentVehicleSelection(newSelection);
      lastSelectedVehicle.current = null;
    } else {
      if (e && e.nativeEvent.shiftKey && lastSelectedVehicle.current) {
        // handle Shift + select
        const vehiclesToAdd = getVehiclesSelection(id);
        setCurrentVehicleSelection([
          ...new Set([...currentVehicleSelection, ...vehiclesToAdd])
        ]);
      } else {
        setCurrentVehicleSelection([...currentVehicleSelection, id]);
      }
      setSelected(true);

      lastSelectedVehicle.current = id;
    }
  };

  const handleEnable = (id, vehicleId) => {
    const isSelected = vehicleId && selectedVehicles.includes(vehicleId);
    if (isSelected) {
      // deselecting vehicle from PROJECT
      openDeselect(vehicleId, id);
    } else {
      setSelectedVehiclesChange(id, "enable");
    }
  };

  useEffect(() => {
    return () => {
      lastSelectedVehicle.current = null;
    };
  }, []);

  const warnChange = (id, isInCurrentSelection, e) => {
    // if (
    //   !saveDisabled &&
    //   (!currentVehicleSelection.length ||
    //     (extendableId !== "multiple" && !isInCurrentSelection))
    // ) {
    //   setChangesDialog({
    //     visible: true,
    //     type: "select",
    //     id,
    //     isInCurrentSelection
    //   });
    // } else {
    handleChange(id, isInCurrentSelection, e);
    // }
  };

  const openDeselect = (id, typeId) => {
    setShowDeselect(true);
    setDeselect({ id, typeId });
  };

  const handleClose = () => {
    setShowDeselect(false);
    setDeselect("");
  };

  // Get all info about vehicle (locked, empty, scheduled)
  const getVehicleDetails = (vehicleObj, routes) => {
    const vehicleRoutes =
      routes && vehicleObj
        ? routes.filter(route =>
            route.vehicle_id.includes(vehicleObj.vehicle_id)
          )
        : undefined;
    if (vehicleRoutes === undefined) {
      return {
        isLocked: false,
        tasksCount: 0,
        backhaulCount: 0,
        assigned: false
      };
    } else {
      let isLocked = vehicleObj.locked;
      let tasksCount = 0;
      vehicleRoutes.forEach(route => {
        route.stops.forEach(task => {
          if (
            task.type !== "start" &&
            task.type !== "end" &&
            task.type !== "break"
          ) {
            tasksCount++;
          }
        });
      });
      return {
        isLocked,
        tasksCount,
        backhaulCount: vehicleRoutes.length,
        assigned: true
      };
    }
  };

  const { filteredCurrentVehicleSelection } = useMemo(() => {
    const filteredCurrentVehicleSelection =
      vehicleTypes.length === filteredVehicles.length
        ? currentVehicleSelection.filter(selectionId =>
            filteredVehicles.find(vehicle => vehicle.id === selectionId)
          )
        : currentVehicleSelection;
    return {
      filteredCurrentVehicleSelection
    };
  }, [currentVehicleSelection]);

  const handleDeselect = () =>
    setSelectedVehiclesChange(deselect.typeId, "disable"); //deselectVehicle(deselect.id, deselect.typeId);

  function rowRenderer({
    key, // Unique key within array of rows
    index, // Index of row within collection
    style // Style object to be applied to row (to position it)
  }) {
    const el = filteredVehicles[index];
    const routes = solution ? solution.routes : undefined;
    const vehicle = vehicles.find(item => {
      return (
        (item.downgraded && item.id === el.id) ||
        (item.type_id && item.type_id.id === el.id)
      );
    });
    const vehicleDetails = getVehicleDetails(vehicle, routes);

    return (
      <div key={key} style={{ ...style }}>
        <Vehicle
          vehicleType={el}
          vehicle={vehicle}
          quantity={el.quantity}
          index={index}
          vehicleDetails={vehicleDetails}
          editVehicle={warnEdit}
          handleChange={warnChange}
          handleEnable={handleEnable}
          vehicleFilterText={vehicleFilterText}
          activeByListCommand={index === listKeyboardCommands.currentIndex}
        />
      </div>
    );
  }

  useEffect(() => {
    listKeyboardCommands.methods.reset();
    listKeyboardCommands.methods.setListSize(filteredVehicles.length);
  }, [filteredVehicles, listKeyboardCommands.methods]);

  useEffect(() => {
    if (vehicleFilterText === "") {
      listKeyboardCommands.methods.stop();
    } else {
      listKeyboardCommands.methods.start();
    }
  }, [listKeyboardCommands.methods, vehicleFilterText]);

  listKeyboardCommands.methods.setEnterHandler((event, index) => {
    event.preventDefault();
    const vehicleType = filteredVehicles[index];
    const vehicle = vehicles.find(item => {
      return (
        (item.downgraded && item.id === vehicleType.id) ||
        (item.type_id && item.type_id.id === vehicleType.id)
      );
    });
    warnEdit(vehicle?.vehicle_id ? vehicle?.vehicle_id : vehicleType?.id);
  });

  const listWrapperRef = useRef();

  // We do this for making list scrollable
  const vehicleListHeight = Math.max(
    Math.min(filteredVehicles.length * 84, window.innerHeight - 180),
    84
  );

  return (
    <Box mb={10}>
      <VehiclesToolbar
        setFilteredVehicles={setFilteredVehicles}
        sortVehiclesByName={sortVehiclesByName}
        setVehicleFilterText={setVehicleFilterText}
        vehicleFilterText={vehicleFilterText}
        filteredVehicles={filteredVehicles}
        filteredCurrentVehicleSelection={filteredCurrentVehicleSelection}
        warnEdit={warnEdit}
      />
      <Slide direction="up" in={!optionsExpanded}>
        <Box
          className={classes.container}
          pb={5}
          ref={listWrapperRef}
          boxShadow={"inset 0 1px 3px -1px rgba(0,0,0,0.0)"}
        >
          {/* Virtualized list */}
          {!!filteredVehicles.length ? (
            <List
              height={vehicleListHeight}
              rowCount={filteredVehicles.length}
              rowHeight={64}
              rowRenderer={rowRenderer}
              width={448}
              ref={listRef}
              onScroll={e => {
                if (listWrapperRef.current) {
                  if (e.scrollTop > 0) {
                    listWrapperRef.current.style.boxShadow =
                      "inset 0 1px 3px -1px rgba(0,0,0,0.2)";
                  } else {
                    listWrapperRef.current.style.boxShadow =
                      "inset 0 1px 3px -1px rgba(0,0,0,0.0)";
                  }
                }
              }}
              className={classes.vehiclesList}
              scrollToIndex={listKeyboardCommands.currentIndex}
            />
          ) : (
            <Typography className={classes.vehicleSearching}>
              {vehicleFilterText.trim().length > 0 &&
              filteredVehicles.length > 0
                ? "Searching for vehicles..."
                : `No vehicles found for “${vehicleFilterText}”`}
            </Typography>
          )}
        </Box>
      </Slide>

      <UnsavedChangesDialog
        open={changesDialog.visible}
        action={() => {
          switch (changesDialog.type) {
            case "edit":
              editVehicle(changesDialog.id);
              break;
            case "select":
            default:
              handleChange(
                changesDialog.id,
                changesDialog.isInCurrentSelection
              );
              break;
          }
        }}
        handleClose={() => setChangesDialog({ visible: false })}
      />
      <ConfirmDialog
        open={showDeselect}
        handleClose={handleClose}
        action={handleDeselect}
        title={"Disable vehicle"}
        body={
          "Are you sure you want to disable this vehicle? This vehicle will not be taken into account during the optimization."
        }
        modalType="confirmDisableVehicle"
        confirmText={"Disable"}
      />
    </Box>
  );
}

function mapStateToProps(state) {
  return {
    vehicles: state.project.vehicles,
    vehicleTypes: state.project.vehicleTypes,
    userType: state.auth.userData.userType,
    lockedRoutes: state.project.lockedRoutes,
    solution: state.project.solution,
    extendableId: state.extendable.id,
    saveDisabled: state.extendable.saveDisabled,
    disabledVehiclesVisible: state.common.disabledVehiclesVisible,
    currentVehicleSelection: state.common.currentVehicleSelection,
    filteredVehiclesByStatus: state.common.filteredVehicles,
    selectedVehicles: state.project.selectedVehicles,
    projectTabValue: state.common.projectTabValue,
    editType: state.extendable.type
  };
}

export default connect(mapStateToProps, {
  setVehicles,
  setExtendableId,
  setExtendableType,
  setCurrentVehicleSelection,
  toggleDisabledVehiclesVisibility,
  deselectVehicle,
  setSelectedVehiclesChange
})(Vehicles);
